import { Button } from '@components/Button'
import { useNetwork } from '@hooks/useNetwork'
import { useChannelCreate } from '@services/api/graphql/mutations/channelCreate'
import { router } from 'expo-router'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input, Text, TextArea, XStack, YStack } from 'tamagui'

type ToastType = 'success' | 'error'

interface INewMessageFormProps {
  onToast?: (type: ToastType, message: string) => void
}

export function NewMessageForm({ onToast }: INewMessageFormProps) {
  const { t } = useTranslation('Messages')
  const { isConnected } = useNetwork()
  const { handleCreate, loading } = useChannelCreate()
  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Trava síncrona contra double-submit: o `loading` do useMutation é estado
  // assíncrono e não bloqueia um segundo onPress disparado no mesmo tick
  // (ex.: pointer + click no RN-Web), o que criava conversas duplicadas.
  const submittingRef = useRef(false)

  const onSubmit = async () => {
    if (submittingRef.current || loading) return

    if (!subject.trim()) {
      setError(t('new.validations.subjectRequired'))
      return
    }
    if (!text.trim()) {
      setError(t('new.validations.textRequired'))
      return
    }

    if (!isConnected) {
      onToast?.('error', t('new.offline'))
      return
    }

    submittingRef.current = true
    try {
      const result = await handleCreate({
        subject: subject.trim(),
        text: text.trim(),
      })
      if (result?.errors && result.errors.length > 0) {
        onToast?.('error', result.errors[0])
        return
      }
      onToast?.('success', t('new.success'))
      router.push('/channels')
    } catch {
      onToast?.('error', t('new.error'))
    } finally {
      submittingRef.current = false
    }
  }

  return (
    <YStack gap="$spacing-inset-sm" testID="new-message-form">
      <YStack gap="$spacing-inset-nano">
        <Text
          fontFamily="$mulishBold"
          fontSize="$font-size-xxs"
          color="$text-primary"
        >
          {t('new.subjectLabel')}
        </Text>
        <Input
          value={subject}
          onChangeText={(value) => {
            setSubject(value)
            if (error) setError(null)
          }}
          placeholder={t('new.subjectPlaceholder')}
          borderColor={error ? '$danger-300' : '$border-default'}
          backgroundColor="$bg-input"
          fontFamily="$mulishRegular"
          fontSize="$font-size-xxs"
          color="$text-primary"
          placeholderTextColor="$text-placeholder"
          height={44}
          paddingHorizontal="$spacing-inset-xs"
          testID="new-message-form-subject"
        />
      </YStack>
      <TextArea
        value={text}
        onChangeText={(value) => {
          setText(value)
          if (error) setError(null)
        }}
        placeholder={t('new.placeholder')}
        borderColor={error ? '$danger-300' : '$border-default'}
        backgroundColor="$bg-input"
        fontFamily="$mulishRegular"
        fontSize="$font-size-xxs"
        color="$text-primary"
        placeholderTextColor="$text-placeholder"
        minHeight={120}
        testID="new-message-form-text"
      />
      <XStack justifyContent="flex-end">
        <Button
          variant="primary"
          onPress={onSubmit}
          disabled={loading}
          loading={loading}
          testID="new-message-form-submit"
        >
          {t('new.submit')}
        </Button>
      </XStack>
    </YStack>
  )
}
